/**
 * Echo Hunt -- Settings: the weaver's rewrite freedom (2026-10-21).
 *
 * One checkbox: whether the weaver is told it may reword each bullet entirely while bridging, rather than only
 * adding connectives at its edges (`WEAVE_PARAPHRASE_NOTE`).
 *
 * ## Why it is off by default
 *
 * The paraphrase that keeps the user's own phrasing out of the final text happens in the RESHAPE step, fresh
 * every round (`Prompts/Reshape.ts` + `PhraseOverlap.ts`). That step is deterministic-checked and
 * retention-gated. The weaver is neither: there is no retention check on woven text, so widening what the weaver
 * may rewrite also widens what it may quietly erode. This is the experiment, not the mechanism -- turn it on if
 * the reshape's output still reads too close to the source, and turn it off again if events start drifting.
 *
 * It is a profile field rather than a code const so it can be toggled mid-session without a rebuild.
 */
import React from "react";
import {observer_mgl} from "mobx-graphlink";
import {CheckBox, Column, Row, Text} from "react-vcomponents";
import {InfoButton} from "web-vcore";
import {GetMyEchoProfile, GetWeaveParaphraseNote, SetWeaveParaphraseNote_Live} from "../../../EchoHunt/Profiles_Live.js";

export const WeaverPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile to configure the weaver.</Text>;
	}

	const enabled = GetWeaveParaphraseNote();

	return (
		<Column style={{gap: 4}}>
			<Row>
				<Text style={{fontWeight: "bold"}}>Weaver</Text>
				<InfoButton ml={5} text={[
					"The weaver orders the round's bullets into a story and adds connectives at their edges (§8.1).",
					"",
					"It is deliberately NOT instructed to paraphrase, because the reshaping step already retells every",
					"bullet in fresh wording, fresh each round, and checks that no 5-word run of your journal text survives.",
					"That check is deterministic and retention-gated; the weaver is neither, so widening what it may",
					"rewrite also widens what it may quietly erode.",
					"",
					"Turn this on if a woven round still reads too close to how you wrote it. If the events in woven rounds",
					"start drifting from the bullets they came from, turn it back off -- that is the trade it makes.",
				].join("\n")}/>
			</Row>
			<CheckBox text="Let the weaver reword freely (not just bridge)" value={enabled}
				onChange={val=>{
					SetWeaveParaphraseNote_Live(val).catch(ex=>console.error("Failed to save the weaver paraphrase setting:", ex));
				}}/>
			<Text style={{opacity: .6, fontSize: 11}}>
				{enabled
					? "On: woven bullets may be reworded entirely, as long as their events stay the same."
					: "Off (default): the weaver may only bridge between bullets. Paraphrasing happens in the reshape step."}
			</Text>
		</Column>
	);
});
